import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, BookOpen, Phone, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import SEO from "../components/SEO";

function fmtDate(iso, isAr) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(isAr ? "ar-KW" : "en-GB", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null);
    setErr(false);
    api.get(`/blog/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [slug]);

  const t = useMemo(() => ({
    back: isAr ? "← العودة للمدونة" : "← Back to blog",
    minRead: (n) => isAr ? `${n} دقيقة قراءة` : `${n} min read`,
    related: isAr ? "اقرأ أيضاً" : "Related reads",
    cta: isAr ? "تحتاج رأي محامٍ في هذه المسألة؟" : "Need a lawyer's view on this?",
    book: isAr ? "احجز موعداً مع محامٍ" : "Book an appointment",
    wa: isAr ? "تواصل عبر واتساب" : "Chat on WhatsApp",
    by: isAr ? "بقلم" : "By",
    notFound: isAr ? "هذا المقال غير متوفر." : "Article not found.",
  }), [isAr]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-slate-600">{t.notFound}</p>
        <Link to="/blog" className="inline-block mt-6 underline">{t.back}</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  const { post, related } = data;
  const paragraphs = (post.body || "").split(/\n+/).filter(Boolean);

  return (
    <div data-testid="blog-post-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <SEO
        titleAr={`${post.title} — مدونة الدخيل`}
        titleEn={`${post.title} — Aldakheel Blog`}
        descriptionAr={post.excerpt}
        descriptionEn={post.excerpt}
      />

      <Link
        to="/blog"
        data-testid="blog-post-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 mb-10"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t.back}
      </Link>

      {/* HEADER */}
      <div className="border-b pb-10 mb-10" style={{ borderColor: "var(--line)" }}>
        <p className="eyebrow inline-flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" style={{ color: "var(--gold-deep)" }} />
          {post.category}
        </p>
        <h1 dir="auto" className="font-display text-4xl lg:text-5xl mt-3 leading-[1.1] tracking-tight">
          {post.title}
        </h1>
        <p dir="auto" className="mt-5 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>
        <div className="mt-6 flex items-center gap-5 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {fmtDate(post.published_at, isAr)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {t.minRead(post.read_minutes || 3)}
          </span>
          <span>{t.by} {post.author}</span>
        </div>
      </div>

      {/* BODY */}
      <article dir={isAr ? "rtl" : "ltr"} className="space-y-5">
        {paragraphs.map((p, i) => {
          const isHeading = /^[ا-ي\w]/.test(p) && p.length < 80 && !p.endsWith(".") && !p.includes("،") && !p.endsWith("؟");
          if (isHeading && i > 0) {
            return (
              <h2 key={i} className="font-display text-2xl lg:text-3xl tracking-tight mt-10 mb-2">
                {p}
              </h2>
            );
          }
          return (
            <p key={i} className="text-[17px] leading-[2.0] text-slate-800">
              {p}
            </p>
          );
        })}
      </article>

      {/* CTA */}
      <div
        className="mt-16 p-7 border-y flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        style={{ borderColor: "var(--line)" }}
      >
        <p className="font-display text-2xl">{t.cta}</p>
        <div className="flex flex-wrap gap-3">
          <a
            href={`https://wa.me/96569656963?text=${encodeURIComponent(isAr ? "مرحباً، أرغب في حجز استشارة قانونية." : "Hello, I'd like to book a legal consultation.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-post-book"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-sm text-sm btn-gold"
          >
            {t.book}
          </a>
          <a
            href="https://wa.me/96569656963"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-post-whatsapp"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-sm text-sm"
            style={{ background: "var(--wa)", color: "#fff" }}
          >
            <Phone className="w-4 h-4" />
            {t.wa}
          </a>
        </div>
      </div>

      {/* RELATED */}
      {related && related.length > 0 && (
        <section className="mt-16">
          <p className="eyebrow mb-4">{t.related}</p>
          <div className="grid md:grid-cols-3 gap-4">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${p.slug}`}
                className="block border p-5 hover:border-slate-900 transition-colors"
                style={{ borderColor: "var(--line)" }}
              >
                <p className="eyebrow text-[10px]" style={{ color: "var(--gold-deep)" }}>{p.category}</p>
                <p className="font-display text-base mt-2 leading-snug line-clamp-3">{p.title}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
